// EventFilesForm.js – Required Files / Documents tab
import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaPlus, FaTrash, FaSpinner, FaCheckCircle, FaChevronDown, FaSearch, FaFileAlt } from "react-icons/fa";
import { uploadFile } from "../../firebase/fileUpload";
import fileExtensionsData from "../../data/fileExtensions.json";

// Deduplicate by extension name (keep first occurrence)
const ALL_EXTENSIONS = (() => {
  const seen = new Set();
  return fileExtensionsData.filter((item) => {
    const key = item.ext.toUpperCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
})();

// Popular / default extensions shown before any search
const POPULAR_EXTS = ["PDF", "JPEG", "PNG", "DOCX", "MP4"];
const POPULAR = ALL_EXTENSIONS.filter((e) => POPULAR_EXTS.includes(e.ext));

// --- Sample-file accept/preview helpers (2026-06-07) -------------------------
// BUG FIX: the sample <input type="file"> was hardcoded accept="image/*", so a
// row whose File Type was PDF/MP4/DOCX could not pick a sample at all (the OS
// dialog greyed out every non-image), blocking event creation at the last step.
// Now the accept attribute follows the row's chosen type, and the preview
// renders a file chip for non-images instead of a broken <img>.
const IMG_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic", "heif"];
const VID_EXTS = ["mp4", "mov", "avi", "mkv", "webm", "m4v", "3gp"];
const AUD_EXTS = ["mp3", "wav", "aac", "ogg", "m4a", "flac"];

// Build the file-input accept string from the row's selected extension.
// Returns undefined (accept anything) when no type is set.
const acceptFor = (ext) => {
  if (!ext) return undefined;
  const e = String(ext).toLowerCase();
  const dot = `.${e}`;
  if (IMG_EXTS.includes(e)) return `${dot},image/*`;
  if (VID_EXTS.includes(e)) return `${dot},video/*`;
  if (AUD_EXTS.includes(e)) return `${dot},audio/*`;
  return dot; // pdf, docx, xlsx, … — match by extension
};

// True only when the uploaded/selected sample is actually an image so we can
// safely render it with <img>; everything else gets a file chip.
const isImageSample = (previewDataUrl, entry) => {
  if (previewDataUrl) return String(previewDataUrl).startsWith("data:image");
  const url = entry.sampleImageUrl;
  if (!url) return false;
  if (/\.(jpe?g|png|gif|webp|bmp|svg|heic|heif)(\?|$)/i.test(url)) return true;
  return IMG_EXTS.includes(String(entry.type || "").toLowerCase());
};

/**
 * Searchable File Type Dropdown
 */
const FileTypeDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Recalculate position from trigger button
  const updatePosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        // Also check if click is inside the fixed dropdown
        const dropdown = document.getElementById("file-type-dropdown-panel");
        if (dropdown && dropdown.contains(e.target)) return;
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reposition on scroll / resize, close if trigger scrolls out of view
  useEffect(() => {
    if (!open) return;
    updatePosition();

    const onScrollOrResize = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      // Close if the trigger has scrolled out of the viewport
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        setOpen(false);
        setSearch("");
        return;
      }
      setPos({ top: rect.bottom + 4, left: rect.left });
    };

    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePosition]);

  // Focus search input when opening
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const filtered = search.trim()
    ? ALL_EXTENSIONS.filter((item) => {
        const term = search.toLowerCase();
        return (
          item.ext.toLowerCase().includes(term) ||
          item.name.toLowerCase().includes(term) ||
          item.category.toLowerCase().includes(term)
        );
      }).slice(0, 30)
    : POPULAR;

  const currentItem = ALL_EXTENSIONS.find((e) => e.ext === value);

  const handleSelect = useCallback((ext) => {
    onChange(ext);
    setOpen(false);
    setSearch("");
  }, [onChange]);

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between border border-gray-300 rounded px-2 py-1.5 text-sm bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors"
      >
        <span className="flex items-center gap-1.5 truncate">
          <span className="font-semibold text-blue-700">{value}</span>
          {currentItem && (
            <span className="text-gray-400 text-xs truncate hidden sm:inline">
              – {currentItem.name}
            </span>
          )}
        </span>
        <FaChevronDown className={`text-gray-400 text-xs transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          id="file-type-dropdown-panel"
          className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-xl flex flex-col overflow-hidden"
          style={{ width: "300px", maxHeight: "320px", top: `${pos.top}px`, left: `${pos.left}px` }}
        >
          {/* Search input */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50">
            <FaSearch className="text-gray-400 text-xs" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search extensions…"
              className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="text-gray-400 hover:text-gray-600 text-xs"
              >
                ✕
              </button>
            )}
          </div>

          {/* Results */}
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-gray-400">
                No extensions found for "{search}"
              </div>
            )}
            {!search.trim() && (
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-semibold bg-gray-50">
                Popular
              </div>
            )}
            {search.trim() && filtered.length > 0 && (
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-semibold bg-gray-50">
                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
              </div>
            )}
            {filtered.map((item) => (
              <button
                key={item.ext}
                type="button"
                onClick={() => handleSelect(item.ext)}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-blue-50 transition-colors ${
                  value === item.ext ? "bg-blue-50 font-semibold" : ""
                }`}
              >
                <span className="w-14 font-mono font-bold text-blue-700 text-xs">{item.ext}</span>
                <span className="text-gray-700 truncate flex-1">{item.name}</span>
                <span className="text-[10px] text-gray-400 hidden sm:inline">{item.category}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const makeEntry = () => ({
  type: "PDF",
  name: "",
  description: "",
  sampleImageUrl: null,
});

const EventFilesForm = ({ files = [], setFiles, enabled = false, setEnabled }) => {
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [previews, setPreviews] = useState({});

  const addEntry = () => setFiles([...files, makeEntry()]);

  const removeEntry = (i) => {
    setFiles(files.filter((_, idx) => idx !== i));
    setPreviews((prev) => {
      const copy = { ...prev };
      delete copy[i];
      // Shift keys above i down by 1
      const shifted = {};
      Object.keys(copy).forEach((k) => {
        const num = parseInt(k, 10);
        shifted[num > i ? num - 1 : num] = copy[k];
      });
      return shifted;
    });
  };

  const updateField = (i, field, val) => {
    const copy = [...files];
    copy[i] = { ...copy[i], [field]: val };
    setFiles(copy);
  };

  const handleSampleUpload = async (e, i) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Local preview
    const reader = new FileReader();
    reader.onload = (ev) =>
      setPreviews((prev) => ({ ...prev, [i]: ev.target.result }));
    reader.readAsDataURL(file);

    setUploadingIndex(i);
    try {
      const url = await uploadFile(file);
      updateField(i, "sampleImageUrl", url);
    } catch (err) {
      console.error("Sample image upload failed:", err);
    } finally {
      setUploadingIndex(null);
    }
  };

  return (
    <div className="w-full p-4 border rounded-md mt-4 bg-blue-50 shadow-md">
      {/* ── Enable toggle ── */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-blue-200">
        <input
          id="files-enabled"
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="w-5 h-5 accent-blue-600 cursor-pointer"
        />
        <label
          htmlFor="files-enabled"
          className="text-base font-semibold text-gray-700 cursor-pointer select-none"
        >
          Enable Required Files for event registration
        </label>
        {!enabled && (
          <span className="ml-2 text-xs text-gray-400 italic">
            (disabled — check to configure file requirements shown in the mobile app)
          </span>
        )}
      </div>

      {/* ── File area — disabled when checkbox is off ── */}
      <div className={`transition-opacity duration-200 ${
        enabled ? "opacity-100" : "opacity-40 pointer-events-none select-none"
      }`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl lg:text-2xl font-semibold text-gray-700">
            Required Files / Documents
          </h3>
          <button
            type="button"
            onClick={addEntry}
            disabled={!enabled}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaPlus className="text-xs" />
            Add More
          </button>
        </div>

      {files.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-6 border-2 border-dashed border-gray-300 rounded-lg bg-white">
          No file requirements added yet. Click <strong>Add More</strong> to
          specify files participants must submit.
        </p>
      )}

      {files.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm bg-white rounded-lg overflow-hidden shadow-sm">
            <thead>
              <tr className="bg-blue-100 text-gray-700 text-left">
                <th className="px-3 py-2 font-semibold w-8">#</th>
                <th className="px-3 py-2 font-semibold w-32">File Type</th>
                <th className="px-3 py-2 font-semibold w-48">File Name</th>
                <th className="px-3 py-2 font-semibold">Description</th>
                <th className="px-3 py-2 font-semibold w-48">
                  Sample File
                </th>
                <th className="px-3 py-2 font-semibold w-12 text-center">
                  Del
                </th>
              </tr>
            </thead>
            <tbody>
              {files.map((entry, i) => (
                <tr
                  key={i}
                  className="border-t border-gray-200 align-top hover:bg-blue-50 transition-colors"
                >
                  {/* Row number */}
                  <td className="px-3 py-3 text-gray-500 font-medium">
                    {i + 1}
                  </td>

                  {/* File Type */}
                  <td className="px-3 py-3">
                    <FileTypeDropdown
                      value={entry.type}
                      onChange={(val) => updateField(i, "type", val)}
                    />
                  </td>

                  {/* File Name */}
                  <td className="px-3 py-3">
                    <input
                      type="text"
                      placeholder="e.g. Resume, Portfolio"
                      value={entry.name}
                      onChange={(e) => updateField(i, "name", e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </td>

                  {/* Description */}
                  <td className="px-3 py-3">
                    <textarea
                      rows={2}
                      placeholder="Brief description of what this file should contain…"
                      value={entry.description}
                      onChange={(e) =>
                        updateField(i, "description", e.target.value)
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </td>

                  {/* Sample Image */}
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-1">
                      <input
                        type="file"
                        accept={acceptFor(entry.type)}
                        onChange={(e) => handleSampleUpload(e, i)}
                        disabled={uploadingIndex === i}
                        className="text-xs w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      {uploadingIndex === i && (
                        <span className="flex items-center gap-1 text-xs text-blue-600">
                          <FaSpinner className="animate-spin" /> Uploading…
                        </span>
                      )}
                      {uploadingIndex !== i && entry.sampleImageUrl && (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <FaCheckCircle /> Uploaded
                        </span>
                      )}
                      {/* Thumbnail (image) or file chip (pdf/video/doc/…) */}
                      {(previews[i] || entry.sampleImageUrl) &&
                        (isImageSample(previews[i], entry) ? (
                          <img
                            src={previews[i] || entry.sampleImageUrl}
                            alt="sample"
                            className="mt-1 w-20 h-14 object-cover rounded border border-gray-300"
                          />
                        ) : (
                          <a
                            href={entry.sampleImageUrl || undefined}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-flex items-center gap-1 text-xs text-gray-700 bg-gray-100 border border-gray-300 rounded px-2 py-1 hover:bg-gray-200 max-w-[8rem]"
                            title="Open sample file"
                          >
                            <FaFileAlt className="text-gray-500 shrink-0" />
                            <span className="truncate">
                              {(entry.type || "FILE")} sample
                            </span>
                          </a>
                        ))}
                    </div>
                  </td>

                  {/* Delete */}
                  <td className="px-3 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => removeEntry(i)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                      title="Remove entry"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={addEntry}
            disabled={!enabled}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaPlus className="text-xs" />
            Add More
          </button>
        </div>
      )}
      </div> {/* end file area */}
    </div>
  );
};

export default EventFilesForm;
